<template>
    <base-dialog :loading="loading" :value="dialog" :width="660" :fullscreen="isMobile" persistent
        :title="editMode ? $t('common.edit_prescription') : $t('common.add_prescription')"
        @input="val => { if (!val) hide() }" @close="hide">

        <div class="pre-body">
            <category-icon-picker :items="categoriesOptions" :value="category" :loading="loadingCategories"
                @change="onClickCategory" />

            <div v-if="!category" class="pre-hint">{{ $t('exercises.need_select_an_category') }}</div>

            <template v-else>
                <div class="pre-grid2">
                    <kimsa-text-field :label="$t('common.name')" dense force-value :value="name"
                        @change="name = $event" />
                    <kimsa-tags-picker ref="tagsPicker" @change="tags = $event" :default-tags="tags"
                        :label="$t('exercises.tags')" force-value />
                </div>

                <div class="pre-row">
                    <div class="pre-mini">
                        <kimsa-combobox :label="$tc('exercises.weeks', 1)" :value="weeks" dense :items="weeksOptions"
                            @change="weeks = $event" placeholder-class ref="weeks" />
                    </div>
                    <div class="pre-mini">
                        <kimsa-combobox :label="$tc('exercises.sets', 2)" :value="set" dense :items="setOptions"
                            @change="set = $event" placeholder-class ref="set" />
                    </div>
                    <div class="pre-tog">
                        <span class="pre-label pre-label--ghost">&nbsp;</span>
                        <kimsa-toggle class="pre-tog-inner" :value="eachSide" @change="eachSide = $event">
                            <div>
                                <div class="pre-tog-lbl">{{ $t('exercises.each_side') }}</div>
                                <div class="pre-tog-sub">{{ $t('exercises.each_side_hint') }}</div>
                            </div>
                        </kimsa-toggle>
                    </div>
                    <div class="pre-tog">
                        <span class="pre-label pre-label--ghost">&nbsp;</span>
                        <kimsa-toggle class="pre-tog-inner" :value="painScale" @change="painScale = $event">
                            <div class="pre-tog-lbl">{{ $t('common.pain_scale') }}</div>
                        </kimsa-toggle>
                    </div>
                    <div v-if="isStrongCategory" class="pre-tog">
                        <span class="pre-label pre-label--ghost">&nbsp;</span>
                        <kimsa-toggle class="pre-tog-inner" :value="weightMode" @change="weightMode = $event">
                            <div class="pre-tog-lbl">{{ $t('common.weight_mode') }}</div>
                        </kimsa-toggle>
                    </div>
                    <div v-if="isStrongCategory" class="pre-field pre-mini">
                        <label class="pre-label">{{ $t('exercises.max_weight') }}</label>
                        <v-text-field solo flat dense hide-details type="number" min="0" @keypress="onNumberKeypress"
                            inputmode="numeric" v-model="maxWeight" />
                    </div>
                    <div class="pre-field">
                        <label class="pre-label">{{ $t('exercises.pause') }}</label>
                        <div class="pre-pause-row">
                            <button type="button" class="pre-pause" :class="{ empty: !time }"
                                @click="showRestMenu(!!time)">
                                <v-icon size="14">mdi-clock-outline</v-icon>
                                <span>{{ time || $t('common.add') }}</span>
                                <v-icon v-if="time" size="12">mdi-pencil</v-icon>
                            </button>
                            <button v-if="time" type="button" class="pre-pause-del" @click="time = null">
                                <v-icon size="13">$delete</v-icon>
                            </button>
                        </div>
                    </div>
                </div>

                <div class="pre-cfg-wrap">
                    <div class="pre-cfg-title">{{ $t('common.config') }}</div>
                    <kimsa-segmented v-if="weeks > 1" class="mb-3" scrollable dense :items="weekItems"
                        :value="currentWeek" @change="goToWeek" />
                    <series-config-editor ref="config" :category="category" :set.sync="set" :max-weight="maxWeight"
                        :set-max="10" @update:valid="configValid = $event" />
                </div>

                <div class="pre-field">
                    <label class="pre-label">{{ $t('common.comment') }}</label>
                    <v-textarea class="pre-comment" solo flat dense hide-details rows="2" auto-grow
                        :placeholder="$t('common.write_here')" v-model="commentary" />
                </div>
            </template>
        </div>

        <template v-slot:footer>
            <div class="pre-foot">
                <button type="button" class="pre-foot-side" @click="hide">{{ $t('common.cancel') }}</button>
                <v-btn depressed tile color="primary" class="pre-foot-confirm black--text" :disabled="disabledAdd"
                    :loading="loading" @click="onContinueButton">
                    <template v-if="currentWeek < weeks">
                        {{ $tc('exercises.weeks', 1) }} {{ currentWeek + 1 }}
                        <v-icon right size="18">mdi-chevron-right</v-icon>
                    </template>
                    <template v-else>{{ $t('common.save') }}</template>
                </v-btn>
            </div>
        </template>

        <rest-dialog as-menu is-second-dialog @save="time = $event" ref="rest" :value="time" />
    </base-dialog>
</template>

<script>
import { mapActions, mapState } from "vuex";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import RestDialog from "@/components/coach/routines/RestDialog";
import { db } from "@/utils/Firebase";
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import KimsaTagsPicker from '../../common/inputs/KimsaTagsPicker.vue';
import KimsaToggle from "@/components/common/inputs/KimsaToggle";
import BaseDialog from "@/components/common/BaseDialog";
import CategoryIconPicker from "@/components/common/inputs/CategoryIconPicker";
import KimsaSegmented from "@/components/common/inputs/KimsaSegmented";
import SeriesConfigEditor from "@/components/coach/routines/SeriesConfigEditor";
import { validateNumberKey } from "@/utils/Exercises";

export default {
    name: "CreatePrescriptionDialog",
    props: {
        coachId: {
            type: String,
            default: null,
        },
    },
    components: {
        KimsaTextField,
        RestDialog,
        KimsaCombobox,
        KimsaTagsPicker,
        KimsaToggle,
        BaseDialog,
        CategoryIconPicker,
        KimsaSegmented,
        SeriesConfigEditor,
    },
    data(vm) {
        return {
            dialog: false,
            configValid: false,
            editMode: false,
            prescriptionId: null,
            loading: false,
            category: null,
            configs: [],
            name: '',
            setOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
            weeksOptions: [1, 2, 3, 4, 5, 6, 7, 8],
            set: 1,
            weeks: 1,
            currentWeek: 1,
            time: null,
            eachSide: false,
            painScale: false,
            weightMode: false,
            maxWeight: null,
            commentary: '',
            tags: [],
        }
    },
    computed: {
        ...mapState(['categoriesOptions', "loadingCategories"]),
        weekItems(vm = this) {
            return Array.from({ length: vm.weeks }, (_, i) => ({
                name: `${vm.$tc('exercises.weeks', 1)} ${i + 1}`,
                value: i + 1,
            }))
        },
        isStrongCategory(vm = this) {
            const key = vm.categoryKey(vm.category?.name)
            return key === vm.CATEGORY.STRONG || key === vm.CATEGORY.STABILITY || key === vm.CATEGORY.MOBILITY
        },
        disabledAdd(vm = this) {
            return !vm.name || !vm.category || !vm.set || !vm.configValid
        },
    },
    async mounted() {
        const vm = this
        if (vm.categoriesOptions.length === 0) await vm.fetchExerciseCategories()
    },
    methods: {
        ...mapActions(['fetchExerciseCategories']),
        show() {
            const vm = this
            vm.dialog = true
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.category = null
            vm.editMode = false
            vm.prescriptionId = null
            vm.reset()
        },
        reset() {
            const vm = this
            vm.name = ''
            vm.weeks = 1
            vm.currentWeek = 1
            vm.set = 1
            vm.time = null
            vm.eachSide = false
            vm.painScale = false
            vm.weightMode = false
            vm.maxWeight = null
            vm.commentary = ''
            vm.configs = []
            vm.tags = []
        },
        setEditingMode(prescription) {
            const vm = this
            vm.editMode = true
            vm.loadingEditMode = true
            vm.show()

            vm.prescriptionId = prescription.objectID || prescription.id
            vm.name = prescription.name
            vm.category = vm.categoriesOptions.find(category => category.id === prescription.category)
            vm.set = prescription?.config?.sets.length || null
            vm.weeks = prescription.weeks || 1
            vm.time = prescription.time || ''
            vm.eachSide = prescription.eachSide || false
            vm.painScale = prescription.painScale || false
            vm.weightMode = prescription.weightMode || false
            vm.maxWeight = prescription.maxWeight || null
            vm.commentary = prescription.commentary || ''
            vm.tags = prescription.tags || []
            vm.configs = [prescription.config, ...prescription.weekConfigs || []]

            vm.loadingEditMode = false
            vm.$nextTick(() => {
                if (vm.$refs.config) vm.$refs.config.populate(vm.configs[0])
            })
        },
        // CATEGORY
        onClickCategory(category) {
            const vm = this
            vm.category = category
        },
        selectColorCategory(category) {
            return this.category?.name === category.name
        },

        // ADD/EDIT REST
        showRestMenu(edit) {
            const vm = this
            edit ? vm.$refs.rest.showEdit(vm.time) :
                vm.$refs.rest.show()
        },
        // VALIDATIONS
        onNumberKeypress(event) {
            validateNumberKey(event)
        },
        onContinueButton() {
            const vm = this;
            if (vm.currentWeek < vm.weeks) {
                vm.configs[vm.currentWeek - 1] = vm.$refs.config.generateConfig()
                vm.currentWeek++
                if (!vm.configs[vm.currentWeek - 1]) vm.configs.push(vm.$refs.config.generateConfig())
                let config = vm.configs[vm.currentWeek - 1]
                vm.$nextTick(() => {
                    vm.$refs.config.populate(config)
                })
            } else {
                vm.configs[vm.currentWeek - 1] = vm.$refs.config.generateConfig()
                vm.add()
            }
        },
        goToWeek(target) {
            const vm = this
            if (target === vm.currentWeek) return
            vm.configs[vm.currentWeek - 1] = vm.$refs.config.generateConfig()
            for (let i = 0; i < target; i++) {
                if (!vm.configs[i]) vm.configs[i] = vm.$refs.config.generateConfig()
            }
            vm.currentWeek = target
            vm.$nextTick(() => {
                vm.$refs.config.populate(vm.configs[target - 1])
            })
        },
        async add() {
            const vm = this
            await vm.withLoading(async () => {
                let payload = {
                    name: vm.name,
                    commentary: vm.commentary,
                    config: vm.configs[0],
                    weekConfigs: vm.configs.slice(1),
                    eachSide: vm.eachSide,
                    painScale: vm.painScale,
                    weightMode: vm.weightMode,
                    hasTime: !!vm.time,
                    time: vm.time,
                    coachId: vm.coachId,
                    category: vm.category.id,
                    categoryName: vm.category.name,
                    tags: vm.tags,
                    weeks: vm.weeks
                }
                if (vm.isStrongCategory) payload.maxWeight = vm.maxWeight

                let objectID = vm.prescriptionId
                if (vm.editMode) {
                    await db.collection('prescriptions').doc(vm.prescriptionId).update(payload)
                } else {
                    const created = await db.collection('prescriptions').add(payload)
                    objectID = created.id
                }

                vm.$emit('success', { objectID, ...payload })
                vm.toast(vm.$t('common.saved_prescription'))
                vm.hide()
            })
        },
    },
    watch: {

        category(val, oldVal) {
            const vm = this
            if (oldVal !== null) vm.reset()
        },
    }
}
</script>

<style scoped>
.pre-body {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.pre-hint {
    text-align: center;
    font-size: 13px;
    color: var(--v-txtMuted-base);
    padding: 24px 0;
}

.pre-foot {
    display: flex;
}

.pre-foot-side {
    padding: 0 20px;
    min-width: 64px;
    height: 52px;
    background: none;
    border: none;
    border-right: 1px solid var(--v-outline-base);
    font-family: inherit;
    font-size: 14px;
    font-weight: 500;
    color: var(--v-txtSecondary-base);
    cursor: pointer;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.15s;
}

.pre-foot-side:hover {
    color: var(--v-txtPrimary-base);
}

.pre-foot-side .v-icon {
    color: inherit !important;
}

.pre-foot-confirm {
    flex: 1;
    height: 52px !important;
    border-radius: 0 !important;
    font-weight: 700;
}

.pre-grid2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    align-items: start;
}

.pre-row {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
    align-items: flex-start;
}

.pre-mini {
    width: 110px;
    flex-shrink: 0;
}

.pre-field {
    min-width: 0;
}

.pre-label {
    display: block;
    font-size: 13px;
    font-weight: 500;
    color: var(--v-txtSecondary-base);
    margin-bottom: 6px;
}

.pre-label--ghost {
    visibility: hidden;
}

.pre-tog {
    display: flex;
    flex-direction: column;
}

.pre-tog-inner {
    min-height: 40px;
    display: flex;
    align-items: center;
    gap: 8px;
}

.pre-tog-lbl {
    font-size: 13px;
    font-weight: 500;
    color: var(--v-txtPrimary-base);
}

.pre-tog-sub {
    font-size: 11px;
    color: var(--v-txtMuted-base);
}

.pre-pause-row {
    display: flex;
    align-items: center;
    gap: 8px;
}

.pre-pause {
    display: flex;
    align-items: center;
    gap: 6px;
    height: 40px;
    padding: 0 14px;
    border-radius: 8px;
    background: var(--v-accent-base);
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    border: none;
    font-family: inherit;
}

.pre-pause.empty {
    background: var(--v-surface2-base);
    color: var(--v-txtSecondary-base);
    border: 1px solid var(--v-outline-base);
}

.pre-pause .v-icon {
    color: inherit !important;
}

.pre-pause-del {
    width: 32px;
    height: 32px;
    border-radius: 6px;
    background: rgba(232, 92, 92, .12);
    border: 1px solid rgba(232, 92, 92, .3);
    color: var(--v-error-base);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
}

.pre-pause-del .v-icon {
    color: inherit !important;
}

.pre-cfg-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--v-txtSecondary-base);
    padding-bottom: 8px;
    border-bottom: 1px solid var(--v-outline-base);
    margin-bottom: 10px;
}

@media (max-width: 600px) {
    .pre-grid2 {
        grid-template-columns: 1fr;
    }
}

.pre-comment {
    font-size: 13px;
}
</style>